import React, { useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import { DataProviderInstance } from '../../api/DataProvider';
import CustomButton from '../buttons/CustomButton';
import { insuranceCardLabel } from '../../pages/appointments/v2/utils/billingType';
import PatientInsuranceFields, {
    buildInsurancePayload,
    buildInsuranceProviderOptions,
    toInsuranceFormState,
    validateInsuranceForm
} from './PatientInsuranceFields';
import styles from './PatientInsuranceForm.module.css';

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Mini-formulário da carteirinha (docs/features/convenios). O mesmo componente
 * atende a ficha, o popup de cadastro e o drawer da agenda (`compact`).
 *
 * Contrato fixo: { patientId, providers, initial, compact, hideTitle, onSaved, onCancel }
 * hideTitle: dentro de um popup, o título fica no cabeçalho do popup.
 * - providers: CompanyConfig.InsuranceProviders ([{Id, Label, AnsRegistry, Plans:[{Id, Label}]}]);
 *   sem a prop, lê do DataProvider.
 * - initial: carteirinha existente (edição) ou undefined (nova).
 * - onSaved(card): { id, provider_id, provider_name, provider_active, plan_id, plan_name,
 *   card_number, holder_name, valid_until, notes, active, label, expired }.
 */
export default function PatientInsuranceForm({ patientId, providers, initial, compact = false, hideTitle = false, onSaved, onCancel }) {
    const { CompanyConfig, SetPatientInsurance, UpdatePatientInsurance } = DataProviderInstance();
    const providerList = providers || CompanyConfig?.InsuranceProviders || [];
    const isEdit = !!initial?.id;

    const [form, setForm] = useState(() => toInsuranceFormState(initial));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [showMore, setShowMore] = useState(!compact);

    const options = useMemo(() => buildInsuranceProviderOptions(providerList, initial), [providerList, initial]);

    const selectedProvider = options.find((provider) => provider.id === form.provider_id) || null;
    const plans = selectedProvider?.plans || [];

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    };

    const onProviderChange = (value) => {
        // Trocar a operadora zera o plano: ele pertence à operadora
        setForm((prev) => ({ ...prev, provider_id: value, plan_id: '' }));
        setErrors((prev) => ({ ...prev, provider_id: undefined, general: undefined }));
    };

    const validate = () => {
        const next = validateInsuranceForm(form);
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const buildCard = (id) => {
        const plan = plans.find((item) => item.id === form.plan_id) || null;
        const card = {
            id,
            provider_id: form.provider_id,
            provider_name: selectedProvider?.name || selectedProvider?.label || initial?.provider_name || '',
            provider_active: selectedProvider ? selectedProvider.active : 1,
            plan_id: form.plan_id || null,
            plan_name: plan ? plan.label : null,
            card_number: form.card_number.trim(),
            holder_name: form.holder_name.trim() || null,
            valid_until: form.valid_until || null,
            notes: form.notes.trim() || null,
            active: isEdit ? Number(initial.active ?? 1) : 1,
            expired: form.valid_until ? form.valid_until < todayISO() : false
        };
        card.label = insuranceCardLabel(card);
        return card;
    };

    const submit = (event) => {
        if (event && event.preventDefault) event.preventDefault();
        if (saving || !validate()) return;

        setSaving(true);
        const payload = buildInsurancePayload(form, patientId);

        const done = (result, fallbackMessage) => {
            setSaving(false);
            if (result?.status === 'SUCCESS' && !result.error) {
                const id = isEdit ? initial.id : result.data?.id;
                if (onSaved) onSaved(buildCard(id));
            } else {
                setErrors({ general: result?.error || fallbackMessage });
            }
        };

        if (isEdit) {
            UpdatePatientInsurance(
                { ...payload, id: initial.id, active: Number(initial.active ?? 1) },
                (result) => done(result, 'Não foi possível salvar a carteirinha. Tente de novo.')
            );
        } else {
            SetPatientInsurance(payload, (result) => done(result, 'Não foi possível cadastrar a carteirinha. Tente de novo.'));
        }
    };

    const idPrefix = `insurance-card-${initial?.id || 'new'}`;

    return (
        <form className={styles.form} onSubmit={submit} noValidate>
            {!compact && !hideTitle && (
                <h4 className={styles.title}>{isEdit ? 'Editar carteirinha' : 'Nova carteirinha'}</h4>
            )}

            {errors.general && <div className={styles.error} role="alert">{errors.general}</div>}

            <PatientInsuranceFields
                form={form}
                errors={errors}
                options={options}
                disabled={saving}
                showMore={showMore}
                compact={compact}
                idPrefix={idPrefix}
                autoFocusNumber={!isEdit && !compact}
                onProviderChange={onProviderChange}
                onFieldChange={setField}
            />

            {compact && (
                <button type="button" className={styles.moreToggle} onClick={() => setShowMore((v) => !v)}>
                    {showMore ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    {showMore ? 'Menos campos' : 'Mais campos (vencimento, titular, observações)'}
                </button>
            )}

            <div className={styles.footer}>
                {onCancel && (
                    <CustomButton style="primary" variant="text" label="Cancelar" onClick={onCancel} enabled={!saving} />
                )}
                <CustomButton
                    style="main"
                    variant="contained"
                    label={isEdit ? 'Salvar' : 'Salvar carteirinha'}
                    icon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={submit}
                    enabled={!saving}
                />
            </div>

            {/* Enter num campo de uma linha envia o formulário */}
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
    );
}
